import { useEffect, useState } from 'react';
import {
  api,
  resolveAssetUrl,
  type AdminBundle,
  type Rarity,
} from '@/api/client';
import { ErrorBox, Loading, PageTitle } from '@/components/ui';

const RARITY_TINT: Record<Rarity, string> = {
  common: 'var(--ink-muted)',
  rare: 'var(--c-blue)',
  epic: 'var(--c-purple)',
  legendary: 'var(--c-yellow)',
};

export function CombosPage() {
  const [items, setItems] = useState<AdminBundle[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () =>
    api.bundles()
      .then(({ items: next }) => setItems(next))
      .catch((e) => setErr(String(e)));

  useEffect(() => {
    void load();
  }, []);

  if (err) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  return (
    <div>
      <PageTitle title="Shop Combos" />
      <div
        className="card"
        style={{
          padding: '10px 14px',
          marginBottom: 16,
          color: 'var(--ink-muted)',
          fontSize: 12,
          background: 'rgba(34,211,238,0.08)',
          borderColor: 'rgba(34,211,238,0.25)',
        }}
      >
        Each combo grants one Character, one Avatar Frame and one 6-second Finisher.
        “Visible in Shop” controls only the combo offer; its three items can remain
        hidden from individual sale.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((item) => (
          <ComboRow key={item.id} initial={item} onSaved={load} />
        ))}
        {items.length === 0 && (
          <div className="card" style={{ padding: 20, color: 'var(--ink-muted)' }}>
            No imported combos.
          </div>
        )}
      </div>
    </div>
  );
}

function ComboRow({
  initial,
  onSaved,
}: {
  initial: AdminBundle;
  onSaved: () => Promise<void> | void;
}) {
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description ?? '');
  const [rarity, setRarity] = useState<Rarity>(initial.rarity);
  const [priceCoins, setPriceCoins] = useState(initial.priceCoins);
  const [priceGems, setPriceGems] = useState(initial.priceGems);
  const [sortOrder, setSortOrder] = useState(initial.sortOrder);
  const [isActive, setIsActive] = useState(initial.isActive);
  const [busy, setBusy] = useState(false);
  const preview = resolveAssetUrl(initial.bannerUrl ?? initial.character.idleUrl);

  async function save() {
    setBusy(true);
    try {
      await api.patchBundle(initial.id, {
        name,
        description: description || null,
        rarity,
        priceCoins,
        priceGems,
        sortOrder,
        isActive,
      });
      await onSaved();
    } catch (e) {
      alert(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="card"
      style={{
        padding: 14,
        display: 'grid',
        gridTemplateColumns: '110px minmax(260px, 1fr) minmax(250px, 0.9fr)',
        gap: 16,
        borderColor: `${RARITY_TINT[rarity]}55`,
      }}
    >
      <div
        style={{
          height: 130,
          borderRadius: 10,
          overflow: 'hidden',
          background: 'rgba(0,0,0,0.28)',
          border: '1px solid var(--hairline)',
        }}
      >
        {preview && (
          <img
            src={preview}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} />
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Combo description"
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <select
            value={rarity}
            onChange={(e) => setRarity(e.target.value as Rarity)}
            style={{ flex: 1 }}
          >
            {(['common', 'rare', 'epic', 'legendary'] as const).map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
          <input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value))}
            title="Sort order"
            style={{ width: 90 }}
          />
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-muted)', lineHeight: 1.7 }}>
          🧗 {initial.character.name}<br />
          ◈ {initial.avatarFrame.name}<br />
          💥 {initial.finisher.name} · {Number(initial.finisher.data.durationSec) || 6}s
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Combo price — coins</label>
        <input
          type="number"
          min={0}
          value={priceCoins}
          onChange={(e) => setPriceCoins(Number(e.target.value))}
        />
        <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Combo price — gems</label>
        <input
          type="number"
          min={0}
          value={priceGems}
          onChange={(e) => setPriceGems(Number(e.target.value))}
        />
        <label style={{ fontSize: 12 }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />{' '}
          visible in Shop
        </label>
        <button className="btn btn-primary" disabled={busy} onClick={save}>
          {busy ? 'SAVING…' : 'SAVE COMBO'}
        </button>
        {!initial.isActive && <span className="badge badge-ban">CURRENTLY HIDDEN</span>}
      </div>
    </div>
  );
}
